import { Box, DropdownLayoutValueOption } from '@wix/design-system'
import {
  FreeTextQuestionContentComponent,
  ListContentComponent,
  useContent,
  useMetadata,
  useNext,
  usePageLoading,
} from '@wix/funnel-builder-lizard'
import { debounce } from 'debounce'
import React, { FC, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { onboardingPageView } from '../../../bi/onboarding'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { BrandedAppLogoWithBack } from '../../reusable/BrandedAppLogoWithBack'
import { Button } from '../../reusable/Button'
import { CategoryProps } from '../../reusable/Category'
import { AutoCompleteWithOverrides } from '../../reusable/Category/AutoCompleteWithOverrides'
import s from '../../reusable/Category/Category.scss'
import { CategoryLayout } from '../../reusable/Category/constants'
import { goNext, goSkip, loadCategories } from '../../reusable/Category/state'
import { Suggestions } from '../../reusable/Category/Suggestions'
import { useCategoryHandler } from '../../reusable/Category/useCategoryHandler'
import { FancyAutoComplete } from '../../reusable/FancyAutoComplete'
import { FooterControls, FooterControlsSpacerHeight, FooterControlsType } from '../../reusable/FooterControls'
import { Heading } from '../../reusable/Heading/Heading'
import { VideoLayout } from '../../reusable/VideoLayout/VideoLayout'
import { VisualBox } from '../../reusable/VisualBox/VisualBox'
import { WixIntrosTemplate } from '../../reusable/WixIntrosTemplate'

const debouncedLoadCategories = debounce(loadCategories, 400)

export type TBrandedAppCategories = {
  backgroundColor: string
  backButton: string
  skipButton: string
  nextButton: string
  title: string
  placeholder: string
  image: string
  video: string
  hint: string
  suggestionsLabel: string
  suggestions: ListContentComponent<{
    text: string
  }>
  question: FreeTextQuestionContentComponent<{}>
}
export const BrandedAppCategory: FC<CategoryProps> = props => {
  const next = useNext()
  const { save, category: savedCategory } = useStorageContext()
  const { businessTermService, layout = CategoryLayout.NEW_CONCEPT } = props
  const [{ language }] = useMetadata()

  const { content } = useContent<TBrandedAppCategories>()
  const { question } = content
  const { questionKey } = question
  const [pageLoading] = usePageLoading()

  const [searchParams] = useSearchParams()
  const prefilledInput = searchParams.get(questionKey) || ''

  const {
    handlers: { handleSuggestionClick, handleSearchChange, handleSearchSelect, handleInputClick, handleClear },
    pageLogger,
    dispatch,
    autoCompleteRef,
    isLargeDesktopScreen,
    state,
  } = useCategoryHandler({
    savedCategory,
    prefilledInput,
    questionKey,
    businessTermService,
  })

  const { search, searchSource, category, categoriesLoading, categories, clickedButton } = state

  const handleNextClick = useCallback(() => {
    if (clickedButton !== undefined || pageLoading) {
      return
    }
    dispatch({ type: 'buttonClicked', clickedButton: 'primary' })
    goNext({ category, pageLogger, language, questionKey, next, save })
  }, [dispatch, clickedButton, category, pageLogger, language, questionKey, next, save, pageLoading])

  const handleSkipClick = useCallback(() => {
    if (clickedButton !== undefined || pageLoading) {
      return
    }
    dispatch({ type: 'buttonClicked', clickedButton: 'skip' })
    goSkip({ pageLogger, next, questionKey, language, save })
  }, [dispatch, clickedButton, pageLogger, next, questionKey, language, save, pageLoading])

  const handleBackClick = useCallback(() => {
    if (clickedButton !== undefined) {
      return
    }
    dispatch({ type: 'buttonClicked', clickedButton: 'back' })
    void pageLogger.generalAction({ action: 'back' })
    window.history.back()
  }, [clickedButton, dispatch, pageLogger])

  useEffect(() => {
    void pageLogger.screenView({ initialData: prefilledInput })
  }, [pageLogger])

  useEffect(() => {
    void debouncedLoadCategories({
      search,
      searchSource,
      dispatch,
      businessTermService,
      pageLogger,
      language,
    })
  }, [search, searchSource, dispatch, pageLogger, language, businessTermService])

  useEffect(() => {
    void onboardingPageView({ contentLanguage: language })
  }, [language])

  useEffect(() => {
    autoCompleteRef.current?.focus()
    autoCompleteRef.current?.showOptions()
  }, [])

  const categoriesOptions: DropdownLayoutValueOption[] = categories.map(category => ({
    id: category.displayName || '',
    value: category.displayName || '',
  }))

  const autocomplete = (
    <>
      <Box gap="SP3" verticalAlign="bottom">
        <Box direction="vertical" flexGrow="1">
          <FancyAutoComplete
            ref={autoCompleteRef}
            value={search || ''}
            selectedId={category?.businessTerm?.displayName}
            onChange={handleSearchChange}
            status={categoriesLoading ? 'loading' : undefined}
            options={categoriesOptions}
            placeholder={content.placeholder}
            size="large"
            onSelect={handleSearchSelect}
            onInputClicked={handleInputClick}
            maxHeightPixels="300"
            className={s.autoComplete}
            border="bottomLine"
            onClear={categoriesLoading ? undefined : handleClear}
            customRenderer={AutoCompleteWithOverrides}
          />
        </Box>
        <Button
          skin={layout === CategoryLayout.WIX_INTROS ? 'standard' : undefined}
          disabled={!category}
          size={isLargeDesktopScreen ? 'large' : 'medium'}
          onClick={handleNextClick}
          label={content.nextButton}
          isLoading={clickedButton === 'primary'}
        />
      </Box>
      <Suggestions
        visible={!category}
        label={content.suggestionsLabel}
        items={content.suggestions.items}
        onItemClick={handleSuggestionClick}
      />
    </>
  )

  const mainContent = (
    <Box direction="vertical" className={s.autoCompleteContainer}>
      <Box marginBottom="SP5">
        <Heading size="extraLarge" className={s.title}>
          {content.title}
        </Heading>
      </Box>
      {autocomplete}
    </Box>
  )

  const video =
    (content.video && content.hint && (
      <VisualBox
        visualType="video"
        backgroundColor={content.backgroundColor}
        src={content.video}
        description={content.hint}
      />
    )) ||
    null

  const footerControls = (
    <FooterControls
      backText={content.backButton}
      backOnClick={handleBackClick}
      skipText={content.skipButton}
      skipOnClick={handleSkipClick}
      type={FooterControlsType.TRANSPARENT}
      spacerHeight={FooterControlsSpacerHeight.NONE}
      isVisible
    />
  )

  // `layout`-prop solution is good enough.
  // Until we get more requirements on BI events for Wix Intros Category page
  // reusing existing events/events names is a good solution.
  if (layout === CategoryLayout.WIX_INTROS) {
    return (
      <WixIntrosTemplate withLogo>
        <Heading size="xxl" className={s.title}>
          {content.title}
        </Heading>
        <Box direction="vertical" width="100%" maxWidth="500px" marginTop="SP6">
          {autocomplete}
        </Box>
        {footerControls}
      </WixIntrosTemplate>
    )
  }

  if (layout === CategoryLayout.NEW_CONCEPT) {
    return (
      <Box height="100vh" overflow="hidden">
        <BrandedAppLogoWithBack />
        <VideoLayout mainContent={mainContent} video={video} />
        {footerControls}
      </Box>
    )
  }

  return null
}
